import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { AlertTriangle, Ban, Check, Siren, X } from "lucide-react";
import { toast } from "sonner";
import { api, errorMessage } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useT } from "@/lib/i18n";
import { useLiveSocket } from "@/lib/useLiveSocket";
import { fmtTime } from "@/lib/format";
import { cn } from "@/lib/utils";

export function AlertStrip() {
  const [live, setLive] = useState<any[]>([]);
  const [dismissed, setDismissed] = useState<Set<string>>(new Set());
  const { can } = useAuth();
  const t = useT();
  const canDecide = can("reconciliation:decide");
  const qc = useQueryClient();
  // A reviewed alert is a permanent decision: ACCEPTED keeps the scorecard penalty,
  // DISMISSED clears it for good. Hiding with X is only cosmetic and comes back.
  const review = async (id: string, verdict: "ACCEPTED" | "DISMISSED") => {
    setDismissed((s) => new Set(s).add(id));
    try {
      await api.post(`/events/${id}/review`, { verdict });
      qc.invalidateQueries({ queryKey: ["scorecard"] });
      qc.invalidateQueries({ queryKey: ["alerts"] });
      toast.success(verdict === "DISMISSED"
        ? "Marked as a false positive — it no longer counts against the driver"
        : "Alert confirmed");
    } catch (e) {
      toast.error(errorMessage(e));
      setDismissed((s) => { const n = new Set(s); n.delete(id); return n; });
    }
  };
  const [flash, setFlash] = useState(false);
  const initial = useQuery({ queryKey: ["alerts"], queryFn: async () => (await api.get("/alerts", { params: { hours: 6, limit: 20 } })).data });
  const rules = useQuery({ queryKey: ["settings"], queryFn: async () => (await api.get("/settings")).data, staleTime: 60000 });
  const notify = (a: any) => {
    const r = rules.data?.alerts || {};
    if (r.sound?.[a.severity]) {
      try {
        const ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
        const o = ctx.createOscillator(); const g = ctx.createGain();
        o.frequency.value = a.severity === "CRITICAL" ? 880 : 520; g.gain.value = 0.08;
        o.connect(g); g.connect(ctx.destination); o.start(); o.stop(ctx.currentTime + (a.severity === "CRITICAL" ? 0.6 : 0.25));
      } catch {}
    }
    if (r.push?.[a.severity] && "Notification" in window && Notification.permission === "granted") {
      new Notification(`Fleetlio ${a.severity}: ${a.label}`, { body: `${a.plate || "vehicle"} · ${fmtTime(a.timestamp)} · ${a.source}` });
    }
  };
  useLiveSocket((msg) => {
    if (msg.type === "alert") {
      setLive((l) => [msg.data, ...l].slice(0, 30));
      notify(msg.data);
      setFlash(true);
      setTimeout(() => setFlash(false), 2500);
    }
  });
  const all = [...live, ...(initial.data || [])].filter((a, i, arr) => arr.findIndex((b) => b.id === a.id) === i && !dismissed.has(a.id)).slice(0, 8);
  const severe = all.some((a) => a.severity === "CRITICAL");
  return (
    <div data-testid="alert-strip" className={cn("tg-card mb-6 overflow-hidden transition-colors", flash && "border-rose-500", severe && "border-rose-500/60")}>
      <div className="flex items-center gap-3 px-4 py-2 border-b border-[#26334d]">
        <Siren className={cn("h-4 w-4", all.length ? "text-rose-400 tg-pulse" : "text-slate-500")} />
        <span className="tg-label">{t("Live alerts · harsh driving & collisions")}</span>
        <span data-testid="alert-strip-count" className="font-mono text-xs text-slate-400">{all.length} active</span>
        <span className="ml-auto text-[10px] font-mono text-slate-500">{t("sources: TrackSolid alarms · camera analytics · manual")}</span>
      </div>
      <div className="flex gap-2 overflow-x-auto px-3 py-2" data-testid="alert-strip-list">
        {all.length === 0 && <div data-testid="alert-strip-empty" className="text-xs text-slate-500 py-1">{t("No active alerts. New alarms appear here instantly.")}</div>}
        {all.map((a) => (
          <div key={a.id} data-testid={`alert-item-${a.id}`} className={cn("shrink-0 flex items-center gap-2 rounded-md border px-3 py-1.5 text-xs tg-enter", a.severity === "CRITICAL" ? "border-rose-500/50 bg-rose-500/10" : "border-amber-500/40 bg-amber-500/10")}>
            <AlertTriangle className={cn("h-3.5 w-3.5", a.severity === "CRITICAL" ? "text-rose-400" : "text-amber-400")} />
            <Link to={a.plate ? `/journeys` : "#"} className="font-mono text-slate-100">{a.plate || "—"}</Link>
            <span className="text-slate-200">{a.label}</span>
            <span className="font-mono text-slate-500">{fmtTime(a.timestamp)} · {a.source}</span>
            {canDecide && (
              <>
                <button data-testid={`alert-accept-${a.id}`} title={t("Confirm: this really was harsh driving")}
                        onClick={() => review(a.id, "ACCEPTED")}
                        className="text-slate-500 hover:text-emerald-300"><Check className="h-3.5 w-3.5" /></button>
                <button data-testid={`alert-reject-${a.id}`} title={t("False positive — do not count it against the driver")}
                        onClick={() => review(a.id, "DISMISSED")}
                        className="text-slate-500 hover:text-amber-300"><Ban className="h-3.5 w-3.5" /></button>
              </>
            )}
            <button data-testid={`alert-dismiss-${a.id}`} title={t("Hide for now")} onClick={() => setDismissed((s) => new Set(s).add(a.id))} className="text-slate-500 hover:text-slate-200"><X className="h-3.5 w-3.5" /></button>
          </div>
        ))}
      </div>
    </div>
  );
}
